import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { API, axios } from '../utils/api';
import { useAuth, destinazioneDopoAccesso, uscitaDaAltraScheda } from '../context/AuthContext';
import { ScenaAccesso, useScenaAccesso } from '../components/accesso/ScenaAccesso';
import CardInstalla from '../components/cliente/CardInstalla';
import {
  VUOTE, classi, conPunto, messaggioErrore, nomeSistema, useInvioUnico, IconaRapido, TestaCard, Avviso, Bottone,
  Campo, CampoPassword, IndicatoreRobustezza, RestaCollegato, Rinvia, useContoAllaRovescia, CodiceSeiCifre,
  passwordTroppoLunga,
} from '../components/accesso/campi';
import { erroriPassword } from '../utils/registrazione';
import { useTraduzioniOspite } from '../utils/linguaOspite';
import { sessioneEraScaduta } from '../utils/sessione';
import { dispositivoConPasskey, dimenticaDispositivo, preparaAccesso, accediConPasskey, aggiornaEmailDispositivo, annullato } from '../utils/passkey';

// Pagina di accesso: bozza approvata design/login-v10.html (scena e stile in components/accesso).
// Linguette Password / Codice via email, password dimenticata dentro la card, accesso rapido (passkey).

// Il reset rifiutato per il codice ("Codice non corretto: restano 4 tentativi", "Codice scaduto…"), non per la password
const codiceRifiutato = (err) => {
  const stato = err?.response?.status;
  const dettaglio = err?.response?.data?.detail;
  return stato >= 400 && stato < 500 && typeof dettaglio === 'string' && /codice/i.test(dettaglio);
};
// La pagina del sito da cui si è arrivati al login (percorso, ricerca e ancora), o null
export const paginaDiPrima = () => {
  try {
    const da = new URL(document.referrer);
    if (da.origin !== window.location.origin || /^\/(login|register)/.test(da.pathname)) return null;
    return `${da.pathname}${da.search}${da.hash}`;
  } catch { return null; }
};

const Login = () => {
  const { login, accedi, isAuthenticated, userType, loading } = useAuth();
  // Fuori dall'area del cliente: la lingua è quella del browser (o quella del profilo, se si è già entrati)
  const { t, l } = useTraduzioniOspite();
  const scena = useScenaAccesso('MK Client — Accedi');
  const { rapido, setRapido, scuoti, entra } = scena;
  // true da quando questa pagina fa entrare qualcuno: allora niente rimando immediato alla sua area (prima la
  // proposta dell'accesso rapido e lo schermo "Bentornati")
  const entrando = useRef(false);

  const [metodo, setMetodo] = useState('pw'); // pw | codice | reset
  // vale per password, codice e accesso rapido (l'interruttore c'è in entrambe le linguette)
  const [restaCollegato, setRestaCollegato] = useState(true);

  // email: una sola per password, codice via email e password dimenticata
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [vediPassword, setVediPassword] = useState(false);
  const [avvisoPw, setAvvisoPw] = useState(null); // { testo, ok }
  const navigate = useNavigate();
  useEffect(() => {
    if (sessioneEraScaduta()) {
      setAvvisoPw({ testo: t('accesso.sessione_scaduta') });
      // Scaduta durante l'uso: utils/api.js (alla prima risposta 401) ricarica /login senza dire da dove; la pagina
      // di prima è quella da cui è partito il caricamento (document.referrer, stesso sito). Ci si torna dopo l'accesso.
      const prima = !scena.torna && paginaDiPrima();
      if (prima) navigate({ search: `?torna=${encodeURIComponent(prima)}` }, { replace: true });
    } else if (uscitaDaAltraScheda()) setAvvisoPw({ testo: t('accesso.uscita_altra_scheda') });
  }, []); // eslint-disable-line
  const [attesaPw, setAttesaPw] = useState(false);
  const invioPw = useInvioUnico();

  // Il passo 2 del codice e del reset sta nell'indirizzo (/login?passo=codice, /login?passo=reset), come la
  // registrazione (Register.jsx): il tasto Indietro del browser torna al passo 1 invece di uscire dal login. Il passo 2
  // vale solo se il codice è partito da questa pagina (ricaricando, o aprendo l'indirizzo a mano, si torna al passo 1).
  const location = useLocation();
  const passoUrl = new URLSearchParams(location.search).get('passo');
  const conPasso = (passo) => {
    const p = new URLSearchParams(location.search);
    if (passo) p.set('passo', passo); else p.delete('passo');
    const s = p.toString();
    return s ? `?${s}` : '';
  };
  const { dalPasso1, ...statoSenzaPasso } = location.state || {};
  const vaiAlPasso = (passo) => navigate({ search: conPasso(passo) }, { state: { ...statoSenzaPasso, dalPasso1: true } });
  // al passo 1: con il tasto Indietro della storia se ci si è arrivati da lì, altrimenti sostituendo l'indirizzo
  const tornaAlPasso1 = () => {
    if (!passoUrl) return;
    if (dalPasso1) navigate(-1);
    else navigate({ search: conPasso(null) }, { replace: true, state: statoSenzaPasso });
  };

  // codice via email. emailCodice: l'email a cui è partito il codice. Prima il passo 2 mostrava (e verificava, e
  // "Rinvia" usava) l'email del campo, che è la stessa della linguetta Password: scrivendone un'altra lì, "Codice
  // inviato a zoe@", il codice giusto risultava scaduto e "Rinvia" lo mandava all'altra email
  const [emailCodice, setEmailCodice] = useState(null);
  const passoCodice = passoUrl === 'codice' && emailCodice ? 2 : 1;
  const [cifre, setCifre] = useState(VUOTE);
  const [avvisoCodice, setAvvisoCodice] = useState(null);
  const [attesaInvio, setAttesaInvio] = useState(false);
  const [attesaVerifica, setAttesaVerifica] = useState(false);
  const [secondiRinvio, avviaRinvio] = useContoAllaRovescia();
  const caselleCodice = useRef([]);
  const verificaInCorso = useRef(false);
  const invioCodice = useInvioUnico();

  // password dimenticata (inviatoA: l'email a cui è partito il codice, come emailCodice)
  const [reset, setReset] = useState({ email: '', inviatoA: null, cifre: VUOTE, password: '', conferma: '' });
  const passoReset = passoUrl === 'reset' && reset.inviatoA ? 2 : 1;
  const [vediNuova, setVediNuova] = useState(false);
  const [avvisoReset, setAvvisoReset] = useState(null);
  const [attesaReset, setAttesaReset] = useState(false);
  const [secondiRinvioReset, avviaRinvioReset] = useContoAllaRovescia();
  const caselleReset = useRef([]);
  const invioReset = useInvioUnico();

  // accesso rapido
  const [attesaRapido, setAttesaRapido] = useState(false);
  const [notaRapido, setNotaRapido] = useState('');
  const opzioniAccesso = useRef(null);
  const invioRapido = useInvioUnico();

  useEffect(() => {
    if (passoCodice === 2) caselleCodice.current[0]?.focus();
  }, [passoCodice]);

  useEffect(() => {
    if (passoReset === 2) caselleReset.current[0]?.focus();
  }, [passoReset]);

  // ?passo= senza un codice partito da qui (pagina ricaricata, indirizzo aperto a mano): al passo 1; con il codice
  // (tasto Avanti del browser) la linguetta giusta
  useEffect(() => {
    if (!passoUrl) return;
    if (passoUrl === 'codice' && emailCodice) setMetodo('codice');
    else if (passoUrl === 'reset' && reset.inviatoA) setMetodo('reset');
    else navigate({ search: conPasso(null) }, { replace: true, state: statoSenzaPasso });
  }, [passoUrl]); // eslint-disable-line

  // Le opzioni della passkey si chiedono subito, così il tocco sul bottone apre Face ID senza attese
  const preparaOpzioniAccesso = useCallback(({ email: emailDispositivo, id }) => {
    opzioniAccesso.current = null;
    preparaAccesso(emailDispositivo, id).then((o) => {
      if (o.dispositivo_attivo === false) {
        // passkey tolta dal profilo (anche da un altro dispositivo): il bottone sparisce
        dimenticaDispositivo();
        setRapido((r) => ({ ...r, dispositivo: null }));
        return;
      }
      opzioniAccesso.current = o;
    }).catch(() => {});
  }, [setRapido]);

  useEffect(() => {
    const dispositivo = dispositivoConPasskey();
    if (rapido.supportate && dispositivo?.email) preparaOpzioniAccesso(dispositivo);
  }, [rapido.supportate, preparaOpzioniAccesso]);

  const cambiaMetodo = (m) => {
    setMetodo(m);
    setAvvisoPw(null);
  };

  // ---------------------------------------------------------------- accesso rapido
  // Bottone "Accedi con Face ID / impronta": se annullato si torna alle linguette senza avvisi
  const entraConPasskey = async () => {
    if (!rapido.dispositivo || !invioRapido.blocca()) return;
    const { dispositivo } = rapido;
    const pronte = opzioniAccesso.current;
    opzioniAccesso.current = null;
    setNotaRapido('');
    setAttesaRapido(true);
    try {
      const dati = await accediConPasskey(pronte, dispositivo.email);
      entrando.current = true;
      entra(accedi(dati, restaCollegato), { proponi: false });
    } catch (err) {
      invioRapido.sblocca();
      setAttesaRapido(false);
      if (err?.response?.status === 401) {
        // passkey tolta (dal profilo o dal server): il bottone sparisce
        dimenticaDispositivo();
        setRapido((r) => ({ ...r, dispositivo: null }));
        setNotaRapido(t('accesso.rapido_non_attivo'));
        return;
      }
      if (!annullato(err)) setNotaRapido(t('accesso.rapido_non_riuscito'));
      preparaOpzioniAccesso(dispositivo);
    }
  };

  // ---------------------------------------------------------------- password
  const accediConPassword = async (e) => {
    e.preventDefault();
    if (!invioPw.blocca()) return; // doppio Invio: una richiesta sola
    setAvvisoPw(null);
    setAttesaPw(true);
    entrando.current = true;
    const esito = await login(email, password, restaCollegato);
    if (esito.success) {
      // l'email con cui si entra è quella buona: il dispositivo la ricorda per l'accesso rapido (dopo un cambio di
      // email quella vecchia non trova più la passkey)
      aggiornaEmailDispositivo(esito.user_info?.email || email);
      entra(esito);
      return;
    }
    entrando.current = false;
    invioPw.sblocca();
    setAttesaPw(false);
    // 5xx e rete: AuthContext dà già "Il servizio non risponde: riprovate tra poco."
    const testo = esito.status === 401
      ? t('accesso.non_corretti')
      : conPunto(esito.error || t('accesso.accesso_non_riuscito'));
    setAvvisoPw({ testo });
    scuoti();
  };

  // ---------------------------------------------------------------- codice via email
  // Risposta del server: { ok: true } oppure { ok: true, limite: true } (troppi codici: non ne arriva un altro)
  const chiediCodice = async (indirizzo) => {
    const r = await axios.post(`${API}/auth/codice/invia`, { email: indirizzo });
    setCifre(VUOTE);
    avviaRinvio();
    return r?.data || {};
  };

  const inviaCodice = async (e) => {
    e.preventDefault();
    if (!invioCodice.blocca()) return; // doppio clic o Invio: un codice solo (il secondo annullava il primo)
    setAvvisoCodice(null);
    setAttesaInvio(true);
    const indirizzo = email.trim();
    try {
      const { limite } = await chiediCodice(indirizzo);
      setEmailCodice(indirizzo);
      vaiAlPasso('codice');
      // al passo 2 si può ancora scrivere un codice ricevuto prima, se non è scaduto
      if (limite) { setAvvisoCodice({ testo: t('accesso.troppi_codici') }); scuoti(); }
    } catch (err) {
      setAvvisoCodice({ testo: messaggioErrore(err, t('accesso.invio_non_riuscito'), l) });
      scuoti();
    } finally {
      setAttesaInvio(false);
      invioCodice.sblocca();
    }
  };

  const rinviaCodice = async () => {
    if (secondiRinvio > 0 || !invioCodice.blocca()) return;
    setAttesaInvio(true);
    try {
      const { limite } = await chiediCodice(emailCodice);
      setAvvisoCodice(limite ? { testo: t('accesso.troppi_codici') } : { testo: t('accesso.nuovo_codice_inviato'), ok: true });
      if (limite) scuoti();
      caselleCodice.current[0]?.focus();
    } catch (err) {
      setAvvisoCodice({ testo: messaggioErrore(err, t('accesso.invio_non_riuscito'), l) });
      scuoti();
    } finally {
      setAttesaInvio(false);
      invioCodice.sblocca();
    }
  };

  const verificaCodice = async (codice) => {
    if (verificaInCorso.current) return;
    const c = codice ?? cifre.join('');
    if (c.length !== 6) {
      setAvvisoCodice({ testo: t('accesso.inserite_sei_cifre') });
      scuoti();
      return;
    }
    verificaInCorso.current = true;
    setAvvisoCodice(null);
    setAttesaVerifica(true);
    try {
      const r = await axios.post(`${API}/auth/codice/verifica`, { email: emailCodice, codice: c });
      aggiornaEmailDispositivo(r.data?.user_info?.email || emailCodice);
      entrando.current = true;
      entra(accedi(r.data, restaCollegato)); // si resta bloccati: il codice è già stato usato
    } catch (err) {
      verificaInCorso.current = false;
      setAvvisoCodice({ testo: messaggioErrore(err, t('accesso.verifica_non_riuscita'), l) });
      setCifre(VUOTE);
      caselleCodice.current[0]?.focus();
      setAttesaVerifica(false);
      scuoti();
    }
  };

  const cambiaEmailCodice = () => {
    tornaAlPasso1();
    setCifre(VUOTE);
    setAvvisoCodice(null);
  };

  // ---------------------------------------------------------------- password dimenticata
  const apriReset = () => {
    setReset({ email, inviatoA: null, cifre: VUOTE, password: '', conferma: '' });
    setAvvisoReset(null);
    setMetodo('reset');
  };

  // "Torna all'accesso" (e la password salvata): via anche il passo dall'indirizzo
  const chiudiReset = () => {
    setMetodo('pw');
    if (passoUrl === 'reset') tornaAlPasso1();
  };

  // Stessa risposta del codice di accesso: con { limite: true } non arriva un altro codice
  const chiediCodiceReset = async (indirizzo) => {
    const r = await axios.post(`${API}/auth/password-reset/request`, { email: indirizzo });
    avviaRinvioReset();
    return r?.data || {};
  };

  const inviaCodiceReset = async (e) => {
    e.preventDefault();
    if (!invioReset.blocca()) return;
    setAvvisoReset(null);
    setAttesaReset(true);
    const indirizzo = reset.email.trim();
    try {
      const { limite } = await chiediCodiceReset(indirizzo);
      setReset((r) => ({ ...r, inviatoA: indirizzo, cifre: VUOTE }));
      vaiAlPasso('reset');
      if (limite) { setAvvisoReset({ testo: t('accesso.troppi_codici') }); scuoti(); }
    } catch (err) {
      setAvvisoReset({ testo: messaggioErrore(err, t('accesso.invio_non_riuscito'), l) });
      scuoti();
    } finally {
      setAttesaReset(false);
      invioReset.sblocca();
    }
  };

  const rinviaCodiceReset = async () => {
    if (secondiRinvioReset > 0 || !invioReset.blocca()) return;
    setAttesaReset(true);
    try {
      const { limite } = await chiediCodiceReset(reset.inviatoA);
      setReset((r) => ({ ...r, cifre: VUOTE }));
      setAvvisoReset(limite ? { testo: t('accesso.troppi_codici') } : { testo: t('accesso.nuovo_codice_inviato'), ok: true });
      if (limite) scuoti();
    } catch (err) {
      setAvvisoReset({ testo: messaggioErrore(err, t('accesso.invio_non_riuscito'), l) });
    } finally {
      setAttesaReset(false);
      invioReset.sblocca();
    }
  };

  const salvaNuovaPassword = async (e) => {
    e.preventDefault();
    const codice = reset.cifre.join('');
    // stessa regola della registrazione e del server
    const errore = codice.length !== 6 ? t('accesso.inserite_sei_cifre')
      : passwordTroppoLunga(reset.password) ? t('accesso.password_lunga')
        : erroriPassword({ password: reset.password, conferma: reset.conferma }, l)?.testo;
    if (errore) { setAvvisoReset({ testo: errore }); scuoti(); return; }
    if (!invioReset.blocca()) return;

    setAvvisoReset(null);
    setAttesaReset(true);
    try {
      await axios.post(`${API}/auth/password-reset/verify`, { email: reset.inviatoA, code: codice, new_password: reset.password });
      setEmail(reset.inviatoA);
      setPassword('');
      chiudiReset();
      setAvvisoPw({ testo: t('accesso.password_aggiornata'), ok: true });
    } catch (err) {
      setAvvisoReset({ testo: messaggioErrore(err, t('accesso.aggiornamento_non_riuscito'), l) });
      scuoti();
      // codice sbagliato o scaduto: caselle vuote e fuoco sulla prima, come nel codice di accesso (la password resta)
      if (codiceRifiutato(err)) {
        setReset((r) => ({ ...r, cifre: VUOTE }));
        caselleReset.current[0]?.focus();
      }
    } finally {
      setAttesaReset(false);
      invioReset.sblocca();
    }
  };

  const azione = (testo, onClick, testid) => (
    <a href={`#${testid}`} role="button" data-testid={testid} onClick={(e) => { e.preventDefault(); onClick(); }}>{testo}</a>
  );

  // Già entrati (aprendo /login da un segnalibro o con il tasto Indietro): alla propria area, non ai moduli (o alla
  // pagina di ?torna=, se è del proprio ruolo)
  if (!loading && isAuthenticated && !entrando.current) return <Navigate to={destinazioneDopoAccesso(scena.torna, userType)} replace />;

  return (
    <ScenaAccesso scena={scena}>
      <TestaCard etichetta={t(`accesso.saluto.${scena.ora}`)} titolo={t('accesso.titolo')} corsivo={t('accesso.titolo_corsivo')}
        sotto={t('accesso.sotto')} />

      <div className="body">
        {metodo !== 'reset' && rapido.supportate && rapido.dispositivo && (
          <>
            <button type="button" className={classi('btn', 'rapido', attesaRapido && 'busy')} aria-busy={attesaRapido}
              disabled={attesaRapido} onClick={entraConPasskey} data-testid="passkey-accedi">
              <IconaRapido tipo={rapido.piattaforma.icona} />
              <span>{t('accesso.accedi_con', { sistema: nomeSistema(rapido.piattaforma.sistema, t) })}</span>
            </button>
            <div className="oppure">{t('accesso.oppure')}</div>
          </>
        )}
        {metodo !== 'reset' && notaRapido && <p className="nota">{notaRapido}</p>}
        {metodo !== 'reset' && (
          <div className={classi('tabs', metodo === 'codice' && 'codice')} role="tablist" aria-label={t('accesso.metodo')}>
            <button type="button" role="tab" aria-selected={metodo === 'pw'} className={metodo === 'pw' ? 'on' : undefined}
              onClick={() => cambiaMetodo('pw')}>{t('accesso.linguetta_password')}</button>
            <button type="button" role="tab" aria-selected={metodo === 'codice'} className={metodo === 'codice' ? 'on' : undefined}
              onClick={() => cambiaMetodo('codice')}>{t('accesso.linguetta_codice')}</button>
          </div>
        )}

        <form className={classi('metodo', metodo === 'pw' && 'on')} onSubmit={accediConPassword} data-testid="login-form">
          <Avviso avviso={avvisoPw} />
          <Campo id="login-email" etichetta={t('accesso.email')} type="email" autoComplete="username" inputMode="email" required
            valore={email} onChange={setEmail} data-testid="login-email" />
          <CampoPassword id="login-password" autoComplete="current-password" required valore={password} onChange={setPassword}
            visibile={vediPassword} onVisibile={() => setVediPassword((v) => !v)} data-testid="login-password" />
          <div className="row">
            <RestaCollegato acceso={restaCollegato} onChange={setRestaCollegato} />
            {azione(t('accesso.password_dimenticata'), apriReset, 'forgot-password-link')}
          </div>
          <Bottone attesa={attesaPw} data-testid="login-submit">{t('accesso.accedi')}</Bottone>
        </form>

        <form className={classi('metodo', metodo === 'codice' && 'on')} onSubmit={passoCodice === 1 ? inviaCodice : (e) => { e.preventDefault(); verificaCodice(); }}>
          <Avviso avviso={avvisoCodice} />
          {passoCodice === 1 ? (
            <>
              <p className="sent intro">{t('accesso.codice_intro')}</p>
              <Campo id="codice-email" etichetta={t('accesso.email')} type="email" autoComplete="email" inputMode="email" required
                valore={email} onChange={setEmail} data-testid="codice-email" />
              <div className="row">
                <RestaCollegato acceso={restaCollegato} onChange={setRestaCollegato} />
              </div>
              <Bottone attesa={attesaInvio} data-testid="codice-invia">{t('accesso.invia_codice')}</Bottone>
            </>
          ) : (
            <>
              <p className="sent">
                {t('accesso.codice_inviato_a')} <b>{emailCodice}</b>. {t('accesso.controllate_spam')}{' '}
                <Rinvia secondi={secondiRinvio} onClick={rinviaCodice} />{' · '}
                {azione(t('accesso.cambia_email'), cambiaEmailCodice, 'cambia-email')}
              </p>
              <CodiceSeiCifre cifre={cifre} onChange={setCifre} onCompleto={verificaCodice} caselle={caselleCodice} />
              <Bottone attesa={attesaVerifica} data-testid="codice-entra">{t('accesso.entra')}</Bottone>
            </>
          )}
        </form>

        {metodo === 'reset' && (
          <form className="metodo on" onSubmit={passoReset === 1 ? inviaCodiceReset : salvaNuovaPassword}>
            <Avviso avviso={avvisoReset} />
            {passoReset === 1 ? (
              <>
                <p className="sent intro">{t('accesso.reset_intro')}</p>
                <Campo id="reset-email" etichetta={t('accesso.email')} type="email" autoComplete="email" inputMode="email" required
                  valore={reset.email} onChange={(v) => setReset((r) => ({ ...r, email: v }))} data-testid="reset-email" />
                <Bottone attesa={attesaReset} data-testid="reset-send-code">{t('accesso.invia_codice')}</Bottone>
              </>
            ) : (
              <>
                <p className="sent">
                  {t('accesso.codice_inviato_a')} <b>{reset.inviatoA}</b>. {t('accesso.controllate_spam')}{' '}
                  <Rinvia secondi={secondiRinvioReset} onClick={rinviaCodiceReset} />
                </p>
                <CodiceSeiCifre cifre={reset.cifre} onChange={(c) => setReset((r) => ({ ...r, cifre: c }))} caselle={caselleReset} />
                <CampoPassword id="reset-password" etichetta={t('accesso.nuova_password')} autoComplete="new-password" required
                  valore={reset.password} onChange={(v) => setReset((r) => ({ ...r, password: v }))}
                  visibile={vediNuova} onVisibile={() => setVediNuova((v) => !v)} data-testid="reset-new-password" />
                <IndicatoreRobustezza password={reset.password} />
                <CampoPassword id="reset-conferma" etichetta={t('accesso.ripetete_password')} autoComplete="new-password" required
                  valore={reset.conferma} onChange={(v) => setReset((r) => ({ ...r, conferma: v }))}
                  visibile={vediNuova} data-testid="reset-confirm-password" />
                <Bottone attesa={attesaReset} data-testid="reset-submit">{t('accesso.salva_password')}</Bottone>
              </>
            )}
            <p className="indietro">{azione(t('accesso.torna_accesso'), chiudiReset, 'torna-accesso')}</p>
          </form>
        )}

        <div className="links">
          {t('accesso.no_account')} <Link to="/register">{t('accesso.registratevi_cliente')}</Link>
          <span className="collab">{t('accesso.siete_collaboratore')} <Link to="/register-collaboratore">{t('accesso.registratevi_qui')}</Link></span>
        </div>

        {/* «Installate l'app»: qui la vede anche chi non è ancora entrato, ed è il momento giusto — chi sta per
            accedere dal telefono è chi poi dovrà ritrovare il sito domani (components/cliente/CardInstalla.jsx) */}
        <CardInstalla />
      </div>
    </ScenaAccesso>
  );
};

export default Login;
